import { useTranslation } from "react-i18next";
import { CountUp, stagger } from "../../shared/motion";
import {
  formatCount,
  formatMinutes,
  formatVolume,
  useLiveStats,
  volumeUnit,
} from "../../shared/lib/useLiveStats";

// Static figures from the design, used for the prerender and whenever a live field is missing
// or zero (a fresh database must not show "0 tadbirkor").
const FALLBACK = { merchants: 1247, contracts: 38400, volume: 4.2e9, minutes: 2 };

const pos = (n: number | null | undefined): n is number => typeof n === "number" && n > 0;

// The hero's 4-up stats row (prototype 177–182). Each figure counts up once when it scrolls in;
// live numbers from the public stats endpoint replace the fallbacks after hydration.
export function HeroStats() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const live = useLiveStats();

  const contracts = pos(live?.contracts) ? live.contracts : null;
  const volume = pos(live?.managed_volume) ? live.managed_volume : FALLBACK.volume;
  const unit = volumeUnit(volume, lang);

  const items = [
    {
      value: pos(live?.merchants) ? live.merchants : FALLBACK.merchants,
      format: formatCount,
      label: t("stats.s1_label"),
    },
    {
      value: contracts ?? FALLBACK.contracts,
      format: formatCount,
      // "+" only on the rounded design figure, not on a live exact count.
      suffix: contracts === null ? "+" : "",
      label: t("stats.s2_label"),
    },
    {
      value: volume,
      format: (n: number) => formatVolume(n, lang, unit),
      label: t("stats.s3_label"),
    },
    {
      value: pos(live?.median_create_minutes) ? live.median_create_minutes : FALLBACK.minutes,
      format: (n: number) => formatMinutes(n, lang),
      label: t("stats.s4_label"),
    },
  ];

  return (
    <div className="stats">
      {items.map((s, i) => (
        <div key={i} className="stat" data-r data-play="count" style={stagger(i)}>
          <b>
            <CountUp value={s.value} format={s.format} suffix={s.suffix} />
          </b>
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  );
}
